import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Grid } from '@mui/material';
import { Close } from '@mui/icons-material';
import { headingBlue, red, lightGrey, mediumGrey } from '../../../assets/colors';
import { buttonStyle } from '../../../utils/commonCSS';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../CustomComponents/CustomComponentsSlice';
import Loader from '../../Loader';
import HttpService from '../../../backend/requestApis';
import { fetchBatchAttendance } from '../../../backend/backendApis';
import moment from 'moment';

const BatchAttendanceDialog = (props) => {
    const { dialogStatus, updateFunc, batchId, batchDetails } = props;
    const dispatch = useDispatch();

    const [attendees, setAttendees] = useState([]);
    const [classes, setClasses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const titleStyle = {
        backgroundColor: headingBlue, 
        color: 'white',
        fontSize: '1rem',
        display: "flex",
        alignItems: 'center',
        padding: "0.4rem 1rem",
        justifyContent: 'space-between'
    };

    const headerCellStyle = {
        padding: '0.4rem 0.5rem', 
        fontFamily: "Helvetica",
        fontSize: "0.85rem", 
        fontWeight: "600",
        textAlign: 'center',
        backgroundColor: lightGrey,
        borderBottom: `1px solid ${mediumGrey}`,
        whiteSpace: 'nowrap'
    };

    const bodyCellStyle = {
        padding: '0.4rem 0.5rem', 
        fontFamily: "Helvetica",
        fontSize: "0.85rem", 
        fontWeight: "400",
        textAlign: 'center',
    };

    useEffect(() => {
        if (dialogStatus && batchId) {
            setIsLoading(true);
            
            let options = {
                url: fetchBatchAttendance,
                method: 'GET',
                params: { batchId: batchId },
                headers: {
                    Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
                }
            };

            HttpService(options).then((resp) => {
                const data = resp?.data?.data || [];
                
                if (Array.isArray(data) && data.length > 0) {
                    // Extract unique classes from the first attendee to build table headers
                    const uniqueClasses = data[0]?.classes?.map(c => ({
                        id: c.classId,
                        date: moment(c.classDate).utcOffset('+05:30').format('DD/MM/YYYY')
                    })) || [];
                    setClasses(uniqueClasses);
                } else {
                    setClasses([]);
                }
                
                setAttendees(Array.isArray(data) ? data : []);
                setIsLoading(false);
            }).catch((err) => {
                console.error("Error fetching batch attendance: ", err);
                dispatch(showSnackBar({ message: 'Error fetching batch attendance. Please try again.', severity: 'error' }));
                setIsLoading(false);
            });
        }
    }, [dialogStatus, batchId]);

    return (
        <Dialog maxWidth='lg' fullWidth open={dialogStatus} onClose={() => updateFunc(false)}>
            <DialogTitle style={titleStyle}>
                <Typography style={{ fontWeight: 600 }}>Batch Attendance Overview</Typography>
                <IconButton onClick={() => updateFunc(false)} style={{ color: 'white', padding: 0 }}>
                    <Close />
                </IconButton>
            </DialogTitle>
             <DialogContent>
                {isLoading && <Loader />}
                <Box style={{ padding: '1.5rem 0rem 0rem 0rem' }}>
                    {batchDetails && (
                        <Box style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '0.4rem', border: `1px solid ${mediumGrey}` }}>
                            <Grid container spacing={2} justifyContent="space-between">
                                <Grid item xs={4} style={{ wordBreak: 'break-word' }}>
                                    <Typography variant="body2" style={{ color: '#888' }}>Batch Name</Typography>
                                    <Typography variant="body1" style={{ fontWeight: 600, color: headingBlue }}>{batchDetails.batchName || '-'}</Typography>
                                </Grid>
                                <Grid item xs={4} style={{ wordBreak: 'break-word' }}>
                                    <Typography variant="body2" style={{ color: '#888' }}>Duration</Typography>
                                    <Typography variant="body1" style={{ fontWeight: 600, color: headingBlue }}>
                                        {batchDetails.startDate ? moment(batchDetails.startDate).utcOffset('+05:30').format('DD/MM/YYYY') : '-'} 
                                        {' to '}
                                        {batchDetails.endDate ? moment(batchDetails.endDate).utcOffset('+05:30').format('DD/MM/YYYY') : '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} style={{ wordBreak: 'break-word' }}>
                                    <Typography variant="body2" style={{ color: '#888' }}>Coordinator</Typography>
                                    <Typography variant="body1" style={{ fontWeight: 600, color: headingBlue }}>
                                        {batchDetails.volunteerName || '-'}
                                        {batchDetails.volunteerCN && (
                                            <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: 400, marginLeft: '0.4rem' }}>
                                                - {batchDetails.volunteerCN}
                                            </span>
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                    
                    <TableContainer component={Paper} style={{ border: `1px solid ${mediumGrey}`, boxShadow: 'none', maxHeight: '55vh', overflowY: 'auto' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{...headerCellStyle, minWidth: '120px', left: 0, position: 'sticky', zIndex: 3}}>Name</TableCell>
                                    <TableCell style={headerCellStyle}>Contact</TableCell>
                                    <TableCell style={headerCellStyle}>District</TableCell>
                                    <TableCell style={headerCellStyle}>State</TableCell>
                                    <TableCell style={headerCellStyle}>% Attendance</TableCell>
                                    {classes.map((c, idx) => (
                                        <TableCell key={idx} style={headerCellStyle}>
                                            {c.date}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(isLoading || attendees.length === 0) ? (
                                    <TableRow>
                                        <TableCell colSpan={5 + classes.length} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                                            {!isLoading && "No attendance records found for this batch."}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    attendees.map((row, index) => {
                                        const percentage = row.attendancePercentage || 0;
                                        return (
                                            <TableRow key={index} hover>
                                                <TableCell style={{ ...bodyCellStyle, left: 0, position: 'sticky', zIndex: 3, backgroundColor: 'white' }}>{row.name}</TableCell>
                                                <TableCell style={bodyCellStyle}>{row.contact || '-'}</TableCell>
                                                <TableCell style={bodyCellStyle}>{row.district || '-'}</TableCell>
                                                <TableCell style={bodyCellStyle}>{row.chapter || '-'}</TableCell>
                                                <TableCell style={{ ...bodyCellStyle, fontWeight: 600, color: percentage >= 75 ? 'green' : (percentage < 50 ? red : 'inherit') }}>
                                                    {percentage}%
                                                </TableCell>
                                                {classes.map((c, idx) => {
                                                    const classRecord = row.classes?.find(cls => cls.classId === c.id);
                                                    const status = classRecord?.status?.toLowerCase();
                                                    const classStatusType = classRecord?.classStatus?.toLowerCase();
                                                    let displayChar = '-';
                                                    let displayColor = 'inherit';

                                                    // If the class itself is not completed, don't show Absent 'A' or Present 'P'.
                                                    // If it's completed, then show 'P' or 'A'.
                                                    if (classStatusType === 'completed') {
                                                        if (status === 'present') {
                                                            displayChar = 'P';
                                                            displayColor = 'green';
                                                        } else if (status === 'absent') {
                                                            displayChar = 'A';
                                                            displayColor = red;
                                                        }
                                                    }
                                                    // For 'scheduled', 'ongoing', 'cancelled', or any other status, display '-'
                                                    return (
                                                        <TableCell key={idx} style={bodyCellStyle}>
                                                            <Typography variant="body2" style={{ fontWeight: 600, color: displayColor }}>
                                                                {displayChar}
                                                            </Typography>
                                                        </TableCell>
                                                    );

                                                })}
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box style={{ padding: '1rem 0rem 0rem 0rem', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button style={{ ...buttonStyle, backgroundColor: red, padding: '0.3rem 1.5rem' }} onClick={() => updateFunc(false)}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default BatchAttendanceDialog;